import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import dragula from 'react-dragula';
import 'react-dragula/dist/dragula.css';
import { ServerPath } from '../../../Constant';
import { _axios } from '../../../utils/axios';
import { ENTITY_TYPES } from './DonationManagementData';

const MAX_ITEMS = 10;
const MIN_CHAR_LEN = 5;
const TITLE_MAX = 50;
const DESC_MAX = 100;
const TITLE_FIELDS = ['title_en', 'title_hi', 'title_mr', 'title_gu'];
const DESC_FIELDS = ['description_en', 'description_hi', 'description_mr', 'description_gu'];
const blankNewItem = () => ({
  _isNew: true,
  _localId: `new-${Math.random().toString(36).slice(2, 9)}`,
  item_id: null,
  title_en: '', title_hi: '', title_mr: '', title_gu: '',
  description_en: '', description_hi: '', description_mr: '', description_gu: '',
});

const EditDonationManagement = ({ show, onHide, row }) => {
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [donationObjectId, setDonationObjectId] = useState(null);
  const [objectMeta, setObjectMeta] = useState({
    object_type: '', object_id: '', object_name: '', object_location: '', is_active: 1,
  });
  const [initialIsActive, setInitialIsActive] = useState(1);

  // Entity selector state (mirrors Create)
  const [entityType, setEntityType] = useState('');
  const [entitySearchInput, setEntitySearchInput] = useState('');
  const [entitySearchResults, setEntitySearchResults] = useState([]);
  const [isSearchingEntities, setIsSearchingEntities] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const searchTimerRef = useRef(null);

  // Fee config
  const [globalFeeType, setGlobalFeeType] = useState('PERCENTAGE');
  const [globalDefaultPercentage, setGlobalDefaultPercentage] = useState(null);
  const [feeType, setFeeType] = useState('PERCENTAGE');
  const [percentageMode, setPercentageMode] = useState('DEFAULT');
  const [customPercentage, setCustomPercentage] = useState('');
  const [initialFeeType, setInitialFeeType] = useState('PERCENTAGE');
  const [initialCustomPercentage, setInitialCustomPercentage] = useState(null);

  // items: ordered array. Tracks both server items and new items added in this session
  const [items, setItems] = useState([]);
  // Snapshot of items at fetch time so we can compute PATCH diffs and original order
  const [originalItems, setOriginalItems] = useState([]);
  // IDs of items the user removed in this session (server items only)
  const [removedItemIds, setRemovedItemIds] = useState([]);

  const dragContainerRef = useRef(null);
  const drakeRef = useRef(null);
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  useEffect(() => {
    if (!show || !row) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const cfgRes = await _axios.get(
          `${ServerPath}/master-donation-objects/config/${row.object_type}/${row.object_id}`
        );
        if (cancelled) return;
        if (!cfgRes.data?.success) {
          toast.error('Failed to load donation config');
          return;
        }
        const cfg = cfgRes.data.data;
        const objId = cfg.donation_object_id;
        setDonationObjectId(objId);
        setObjectMeta({
          object_type: cfg.object_type,
          object_id: cfg.object_id,
          object_name: cfg.object_name,
          object_location: cfg.object_location,
          is_active: cfg.is_active,
        });
        setInitialIsActive(cfg.is_active);
        const cfgFeeType = cfg.fee_type || 'PERCENTAGE';
        const cfgCustomPct = cfg.custom_percentage;
        setFeeType(cfgFeeType === 'FIXED_SLAB' ? 'FIXED_SLAB' : 'PERCENTAGE');
        if (cfgFeeType === 'PERCENTAGE' && cfgCustomPct != null) {
          setPercentageMode('CUSTOM');
          setCustomPercentage(cfgCustomPct);
        } else {
          setPercentageMode('DEFAULT');
          setCustomPercentage('');
        }
        setInitialFeeType(cfgFeeType);
        setInitialCustomPercentage(cfgCustomPct);
        setEntityType(cfg.object_type || '');
        setSelectedEntity({
          object_type: cfg.object_type,
          object_id: cfg.object_id,
          object_name: cfg.object_name,
          object_location: cfg.object_location,
        });

        const itemsRes = await _axios.get(`${ServerPath}/donation-items/object/${objId}`);
        if (cancelled) return;
        if (itemsRes.data?.success) {
          const fetched = (itemsRes.data.data || [])
            .slice()
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map(it => ({
              _isNew: false,
              _localId: `srv-${it.item_id}`,
              item_id: it.item_id,
              title_en: it.title_en || '',
              title_hi: it.title_hi || '',
              title_mr: it.title_mr || '',
              title_gu: it.title_gu || '',
              description_en: it.description_en || '',
              description_hi: it.description_hi || '',
              description_mr: it.description_mr || '',
              description_gu: it.description_gu || '',
            }));
          setItems(fetched);
          setOriginalItems(fetched.map(it => ({ ...it })));
          setRemovedItemIds([]);
        }
      } catch (err) {
        console.error('Edit load error:', err);
        toast.error('Failed to load donation');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [show, row]);

  useEffect(() => {
    if (!show) return;
    (async () => {
      try {
        const res = await _axios.get(`${ServerPath}/convenience-fee/get`);
        if (res.data?.success === 1 && res.data?.data?.length > 0) {
          const cfg = res.data.data[0];
          setGlobalFeeType(cfg.fee_type || 'PERCENTAGE');
          setGlobalDefaultPercentage(cfg.default_percentage);
        }
      } catch (err) {
        console.error('Error fetching global fee config:', err);
      }
    })();
  }, [show]);

  // Reset state on close
  useEffect(() => {
    if (!show) {
      setDonationObjectId(null);
      setObjectMeta({ object_type: '', object_id: '', object_name: '', object_location: '', is_active: 1 });
      setInitialIsActive(1);
      setItems([]);
      setOriginalItems([]);
      setRemovedItemIds([]);
      setIsSubmitting(false);
      setLoading(false);
      setEntityType('');
      setEntitySearchInput('');
      setEntitySearchResults([]);
      setSelectedEntity(null);
      setFeeType('PERCENTAGE');
      setPercentageMode('DEFAULT');
      setCustomPercentage('');
      setInitialFeeType('PERCENTAGE');
      setInitialCustomPercentage(null);
    }
  }, [show]);

  const fetchEntityResults = (type, query) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setIsSearchingEntities(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await _axios.get(`${ServerPath}/admin-notification/favourite-entities`, {
          params: { type, search: query || '', limit: 20 }
        });
        if (data.success === 1 || data.success === true) setEntitySearchResults(data.data || []);
        else setEntitySearchResults([]);
      } catch (err) {
        console.error('Entity search error:', err);
        setEntitySearchResults([]);
      } finally {
        setIsSearchingEntities(false);
      }
    }, 300);
  };

  const handleEntityTypeChange = (e) => {
    setEntityType(e.target.value);
    setEntitySearchInput('');
    setEntitySearchResults([]);
    setSelectedEntity(null);
  };

  const handleEntitySearchChange = (e) => {
    const value = e.target.value;
    setEntitySearchInput(value);
    if (!entityType) return;
    fetchEntityResults(entityType, value);
  };

  const handleEntitySelect = (entity) => {
    if (!entityType) return;
    setSelectedEntity({
      object_type: entityType,
      object_id: entity.entity_id,
      object_name: entity.name,
      object_location: null,
    });
    setEntitySearchInput('');
    setEntitySearchResults([]);
  };

  const handleEntityRemove = () => setSelectedEntity(null);

  // Wire up dragula once the items section is rendered (after loading completes)
  useEffect(() => {
    if (loading) return;
    if (!dragContainerRef.current || drakeRef.current) return;
    const drake = dragula([dragContainerRef.current], {
      moves: (_el, _src, handle) => {
        let node = handle;
        while (node && node !== dragContainerRef.current) {
          if (node.classList && node.classList.contains('drag-handle')) return true;
          node = node.parentNode;
        }
        return false;
      },
      direction: 'vertical',
    });
    drake.on('drop', () => {
      const container = dragContainerRef.current;
      if (!container) return;
      // Capture the new order from the live DOM before reverting.
      const newOrderIds = Array.from(container.children).map(child => child.getAttribute('data-localid'));
      // Revert dragula's DOM mutation so React's virtual DOM stays in sync;
      // React will re-apply the order via setItems below.
      drake.cancel(true);
      const current = itemsRef.current;
      const map = new Map(current.map(it => [it._localId, it]));
      const reordered = newOrderIds.map(id => map.get(id)).filter(Boolean);
      if (reordered.length === current.length) {
        setItems(reordered);
      }
    });
    drakeRef.current = drake;
    return () => {
      drake.destroy();
      drakeRef.current = null;
    };
  }, [loading]);

  const handleItemChange = (localId, field, value) => {
    setItems(prev => prev.map(it => it._localId === localId ? { ...it, [field]: value } : it));
  };

  const handleAddItem = () => {
    if (items.length >= MAX_ITEMS) {
      toast.error(`Maximum ${MAX_ITEMS} donation items allowed`);
      return;
    }
    setItems(prev => [...prev, blankNewItem()]);
  };

  const handleRemoveItem = (localId) => {
    const target = items.find(it => it._localId === localId);
    if (!target) return;
    if (!target._isNew && target.item_id) {
      setRemovedItemIds(prev => [...prev, target.item_id]);
    }
    setItems(prev => prev.filter(it => it._localId !== localId));
  };

  const itemDiffersFromOriginal = (current, original) => {
    const fields = ['title_en', 'title_hi', 'title_mr', 'title_gu',
                    'description_en', 'description_hi', 'description_mr', 'description_gu'];
    return fields.some(f => (current[f] || '') !== (original[f] || ''));
  };

  const buildPatchBody = (current) => {
    const body = {};
    const fields = ['title_en', 'title_hi', 'title_mr', 'title_gu',
                    'description_en', 'description_hi', 'description_mr', 'description_gu'];
    fields.forEach(f => {
      const v = current[f]?.trim();
      body[f] = v ? v : null;
    });
    return body;
  };

  const handleSubmit = async () => {
    if (!selectedEntity) return toast.error('Please select an entity');
    if (items.length > MAX_ITEMS) return toast.error(`Maximum ${MAX_ITEMS} donation items allowed`);

    // Skip purely-empty new rows; existing server rows are always validated.
    const isNewRowEmpty = (it) => Object.entries(it).every(([k, v]) =>
      k.startsWith('_') || k === 'item_id' || !v || !String(v).trim()
    );
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it._isNew && isNewRowEmpty(it)) continue;
      if (!it.title_en || !it.title_en.trim()) {
        return toast.error(`Item #${i + 1}: English title is required`);
      }
      for (const k of TITLE_FIELDS) {
        const v = (it[k] || '').trim();
        if (v && v.length < MIN_CHAR_LEN) {
          return toast.error(`Item #${i + 1}: title must be at least ${MIN_CHAR_LEN} characters`);
        }
      }
      for (const k of DESC_FIELDS) {
        const v = (it[k] || '').trim();
        if (v && v.length < MIN_CHAR_LEN) {
          return toast.error(`Item #${i + 1}: description must be at least ${MIN_CHAR_LEN} characters`);
        }
      }
    }

    if (feeType === 'PERCENTAGE' && percentageMode === 'CUSTOM') {
      const pct = parseFloat(customPercentage);
      if (Number.isNaN(pct) || pct <= 0 || pct > 100) {
        return toast.error('Custom percentage must be between 0 and 100');
      }
    }

    let resolvedFeeType = feeType;
    let resolvedCustomPct = null;
    if (feeType === 'PERCENTAGE' && percentageMode === 'CUSTOM') {
      resolvedCustomPct = parseFloat(customPercentage);
    }

    setIsSubmitting(true);
    try {
      // 1) Upsert entity (covers status change and any entity edits)
      const entityChanged =
        selectedEntity.object_type !== objectMeta.object_type ||
        selectedEntity.object_id !== objectMeta.object_id;
      const feeChanged =
        resolvedFeeType !== initialFeeType ||
        (resolvedCustomPct ?? null) !== (initialCustomPercentage ?? null);
      if (entityChanged || objectMeta.is_active !== initialIsActive || feeChanged) {
        await _axios.post(`${ServerPath}/master-donation-objects`, {
          object_type: selectedEntity.object_type,
          object_id: selectedEntity.object_id,
          object_name: selectedEntity.object_name,
          object_location: selectedEntity.object_location,
          is_active: objectMeta.is_active,
          fee_type: resolvedFeeType,
          custom_percentage: resolvedCustomPct,
        });
      }

      // 2) Delete removed items
      for (const itemId of removedItemIds) {
        try {
          await _axios.delete(`${ServerPath}/donation-items/${itemId}`);
        } catch (err) {
          console.error('Item delete error:', err);
        }
      }

      // 3) PATCH modified existing items
      const originalMap = new Map(originalItems.map(it => [it.item_id, it]));
      for (const it of items) {
        if (it._isNew) continue;
        const orig = originalMap.get(it.item_id);
        if (orig && itemDiffersFromOriginal(it, orig)) {
          try {
            await _axios.patch(`${ServerPath}/donation-items/${it.item_id}`, buildPatchBody(it));
          } catch (err) {
            console.error('Item patch error:', err);
          }
        }
      }

      // 4) POST new items (assign sort_order = current index). Skip empty new rows.
      const createdIdByLocal = new Map();
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (!it._isNew) continue;
        if (!it.title_en || !it.title_en.trim()) continue;
        const payload = {
          title_en: it.title_en.trim(),
          title_hi: it.title_hi?.trim() || null,
          title_mr: it.title_mr?.trim() || null,
          title_gu: it.title_gu?.trim() || null,
          description_en: it.description_en?.trim() || null,
          description_hi: it.description_hi?.trim() || null,
          description_mr: it.description_mr?.trim() || null,
          description_gu: it.description_gu?.trim() || null,
          sort_order: i + 1,
        };
        try {
          const res = await _axios.post(`${ServerPath}/donation-items/object/${donationObjectId}`, payload);
          if (res.data?.success) {
            const newId = res.data.data?.item_id;
            if (newId) createdIdByLocal.set(it._localId, newId);
          }
        } catch (err) {
          console.error('Item create error:', err);
        }
      }

      // 5) Reorder if needed (always send to be safe if anything changed in order)
      const originalOrderIds = originalItems
        .filter(it => !removedItemIds.includes(it.item_id))
        .map(it => it.item_id);
      const finalOrderIds = items
        .map(it => it._isNew ? createdIdByLocal.get(it._localId) : it.item_id)
        .filter(Boolean);
      const orderChanged =
        finalOrderIds.length !== originalOrderIds.length ||
        finalOrderIds.some((id, i) => id !== originalOrderIds[i]);
      if (orderChanged && finalOrderIds.length > 0) {
        try {
          await _axios.put(`${ServerPath}/donation-items/object/${donationObjectId}/reorder`, {
            item_ids: finalOrderIds,
          });
        } catch (err) {
          console.error('Reorder error:', err);
        }
      }

      toast.success('Donation updated successfully');
      onHide(true);
    } catch (err) {
      console.error('Update donation error:', err);
      toast.error('Failed to update donation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={() => onHide(false)} size="xl" centered scrollable>
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="fw-bold">Edit Donation</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        {loading ? (
          <div className="text-center py-5 text-muted">Loading...</div>
        ) : (
          <>
            {/* Select Entity — same layout as Create, fully editable, with Status inline */}
            <Form.Label className="fw-semibold mb-2">Select Entity <span className="text-danger">*</span></Form.Label>
            <Row className="g-3 mb-4 align-items-start">
              <Col md={3}>
                <Form.Label className="small mb-1">Entity Type</Form.Label>
                <Form.Select value={entityType} onChange={handleEntityTypeChange} disabled={!!selectedEntity}>
                  <option value="" disabled hidden>Select Entity Type</option>
                  {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small mb-1">Search Entity</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder={selectedEntity ? 'Entity selected — remove to change' : 'Search entity...'}
                    value={entitySearchInput}
                    onChange={handleEntitySearchChange}
                    disabled={!entityType || !!selectedEntity}
                  />
                  {!selectedEntity && entityType && entitySearchInput.length > 0 && (
                    <div
                      className="position-absolute w-100 bg-white border rounded mt-1 shadow-sm"
                      style={{ zIndex: 1000, maxHeight: 200, overflowY: 'auto' }}
                    >
                      {isSearchingEntities ? (
                        <div className="p-2 text-muted small text-center">Searching...</div>
                      ) : entitySearchResults.length === 0 ? (
                        <div className="p-2 text-muted small text-center">No Result Found</div>
                      ) : (
                        entitySearchResults.map(e => (
                          <div
                            key={e.entity_id}
                            className="p-2"
                            style={{ cursor: 'pointer', fontSize: 14 }}
                            onMouseDown={(ev) => ev.preventDefault()}
                            onClick={() => handleEntitySelect(e)}
                          >
                            {e.name} 
                            {/* <span className="text-muted">({e.favourite_user_count ?? 0})</span> */}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <Form.Label className="small mb-1">Selected Entity</Form.Label>
                <div
                  style={{
                    minHeight: 38, border: '1px solid #E2E8F0', borderRadius: 6,
                    padding: '6px 10px', background: '#fff',
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6
                  }}
                >
                  {!selectedEntity ? (
                    <span className="text-muted small">No entity selected</span>
                  ) : (
                    <span
                      className="d-inline-flex align-items-center"
                      style={{ background: '#6366F1', color: 'white', padding: '4px 10px', borderRadius: 6, fontSize: 13 }}
                    >
                      {selectedEntity.object_name}
                      <button
                        type="button"
                        onClick={handleEntityRemove}
                        className="ms-2 border-0 bg-transparent text-white"
                        style={{ padding: 0, lineHeight: 1, fontSize: 16, fontWeight: 'bold', cursor: 'pointer' }}
                      >×</button>
                    </span>
                  )}
                </div>
              </Col>
              <Col md={2}>
                <Form.Label className="small mb-1">Status</Form.Label>
                <Form.Select
                  value={objectMeta.is_active ? 'ACTIVE' : 'ARCHIVED'}
                  onChange={(e) => setObjectMeta(prev => ({ ...prev, is_active: e.target.value === 'ACTIVE' ? 1 : 0 }))}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="ARCHIVED">Archived</option>
                </Form.Select>
              </Col>
            </Row>

            {/* Fee Type */}
            <Form.Label className="fw-semibold mb-2">Fee Type <span className="text-danger">*</span></Form.Label>
            <div className="border rounded p-3 mb-4" style={{ background: '#fafafa' }}>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Check
                    type="radio"
                    label="Percentage"
                    name="edit-fee-type"
                    checked={feeType === 'PERCENTAGE'}
                    onChange={() => setFeeType('PERCENTAGE')}
                  />
                  <Form.Check
                    type="radio"
                    label="Fixed Amount Slab"
                    name="edit-fee-type"
                    checked={feeType === 'FIXED_SLAB'}
                    onChange={() => setFeeType('FIXED_SLAB')}
                  />
                </Col>
                <Col md={8}>
                  {feeType === 'PERCENTAGE' ? (
                    <div className="border rounded p-2 bg-white">
                      <Form.Check
                        type="radio"
                        name="edit-pct-mode"
                        checked={percentageMode === 'DEFAULT'}
                        onChange={() => { setPercentageMode('DEFAULT'); setCustomPercentage(''); }}
                        label={
                          <span>
                            Default
                            {globalDefaultPercentage != null && globalFeeType === 'PERCENTAGE' && (
                              <span className="text-muted"> ({globalDefaultPercentage}%)</span>
                            )}
                          </span>
                        }
                      />
                      <div className="d-flex align-items-center gap-2 mt-1">
                        <Form.Check
                          type="radio"
                          name="edit-pct-mode"
                          checked={percentageMode === 'CUSTOM'}
                          onChange={() => setPercentageMode('CUSTOM')}
                          label="Custom"
                        />
                        {percentageMode === 'CUSTOM' && (
                          <div className="d-flex align-items-center gap-2 ms-3">
                            <span className="small text-muted">
                              {selectedEntity?.object_name || 'Selected entity'}
                            </span>
                            <Form.Control
                              type="number"
                              size="sm"
                              min="0"
                              max="100"
                              step="0.01"
                              style={{ width: 100 }}
                              value={customPercentage}
                              onChange={(e) => setCustomPercentage(e.target.value)}
                              placeholder="%"
                            />
                            <span>%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="border rounded p-2 bg-white text-muted small">
                      Global slab configuration will be applied to this entity.
                    </div>
                  )}
                </Col>
              </Row>
            </div>

            {/* Donation Items */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="fw-semibold mb-0">
                Donation Items <span className="text-danger">*</span>{' '}
                <span className="text-muted small">({items.length}/{MAX_ITEMS})</span>
              </Form.Label>
              <span className="text-muted small">Drag the handle to reorder</span>
            </div>

            <div ref={dragContainerRef}>
              {items.map((item, idx) => (
                <div
                  key={item._localId}
                  data-localid={item._localId}
                  className="border rounded p-3 mb-3"
                  style={{ background: '#fafafa' }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="drag-handle"
                        title="Drag to reorder"
                        style={{
                          cursor: 'grab', userSelect: 'none', fontSize: 18,
                          padding: '2px 6px', background: '#e5e7eb', borderRadius: 4
                        }}
                      >⋮⋮</span>
                      <strong className="small text-muted">
                        Item #{idx + 1} {item._isNew && <span className="badge bg-info ms-1">new</span>}
                      </strong>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleRemoveItem(item._localId)}
                    >×</button>
                  </div>
                  <Row className="g-3">
                    {[
                      { label: 'Eng', key: 'title_en', required: true },
                      { label: 'Hin', key: 'title_hi' },
                      { label: 'Mar', key: 'title_mr' },
                      { label: 'Guj', key: 'title_gu' },
                    ].map(f => (
                      <Col md={3} key={f.key}>
                        <Form.Label className="small mb-1">
                          Donation Title({f.label}){f.required && <span className="text-danger"> *</span>}
                        </Form.Label>
                        <Form.Control
                          value={item[f.key]}
                          onChange={(e) => handleItemChange(item._localId, f.key, e.target.value)}
                          maxLength={TITLE_MAX}
                          style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                        />
                        <div className="text-end small text-muted">{(item[f.key] || '').length}/{TITLE_MAX}</div>
                      </Col>
                    ))}
                  </Row>
                  <Row className="g-3 mt-1">
                    {[
                      { label: 'Eng', key: 'description_en' },
                      { label: 'Hin', key: 'description_hi' },
                      { label: 'Mar', key: 'description_mr' },
                      { label: 'Guj', key: 'description_gu' },
                    ].map(f => (
                      <Col md={3} key={f.key}>
                        <Form.Label className="small mb-1">Donation Description({f.label})</Form.Label>
                        <Form.Control
                          as="textarea" rows={3}
                          value={item[f.key]}
                          onChange={(e) => handleItemChange(item._localId, f.key, e.target.value)}
                          maxLength={DESC_MAX}
                          style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', resize: 'vertical' }}
                        />
                        <div className="text-end small text-muted">{(item[f.key] || '').length}/{DESC_MAX}</div>
                      </Col>
                    ))}
                  </Row>
                </div>
              ))}
            </div>

            <Button
              variant="primary"
              size="sm"
              onClick={handleAddItem}
              disabled={items.length >= MAX_ITEMS}
            >+ Add Donation Item</Button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0 pb-4">
        <Button
          className="px-5 py-2"
          style={{ backgroundColor: '#6c757d', border: 'none', borderRadius: '8px' }}
          onClick={() => onHide(false)}
          disabled={isSubmitting}
        >Cancel</Button>
        <Button
          className="px-5 py-2"
          style={{ backgroundColor: '#5662F6', border: 'none', borderRadius: '8px' }}
          onClick={handleSubmit}
          disabled={isSubmitting || loading}
        >{isSubmitting ? 'Saving...' : 'Save Changes'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditDonationManagement;
